/**
 * Fiche joueur — modale affichant le profil d'un joueur.
 *
 * Adapte le contenu et les onglets selon le mode détente / compétition.
 * Sur smartphone (compact), s'affiche en plein écran.
 */

import { useState, useEffect } from 'react';
import { PlayerAvatar } from './PlayerAvatar';
import { useResponsive } from '../hooks/useResponsive';
import '../styles/profile-modal.css';
import { BACKEND_URL } from '../config';

const OBSOLETE_CONTACT_TYPES = new Set(['msn', 'icq', 'skype']);

interface PlayerProfileModalProps {
  pseudo: string;
  compet: boolean;
  onClose: () => void;
  onOpenTeam?: (teamId: string | number) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProfileData = Record<string, any>;

const DETENTE_TABS = [
  { id: 'infos', label: 'Infos' },
  { id: 'classements', label: 'Classements' },
  { id: 'stats', label: 'Stats' },
  { id: 'live', label: 'Live' },
];

const COMPET_TABS = [
  { id: 'infos', label: 'Infos' },
  { id: 'classements', label: 'Classements' },
  { id: 'assiduite', label: 'Assiduité' },
  { id: 'defis', label: 'Défis Libres' },
];

function fmtNum(v: unknown): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

function fmtPct(v: unknown): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toFixed(2).replace('.', ',') + ' %';
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  try {
    return new Date(String(v)).toLocaleDateString('fr-FR');
  } catch {
    return String(v);
  }
}

export default function PlayerProfileModal({ pseudo, compet, onClose, onOpenTeam }: PlayerProfileModalProps) {
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('infos');
  const [liveMode, setLiveMode] = useState<'3' | '4' | '5'>('4');
  const { isCompact } = useResponsive();

  const tabs = compet ? COMPET_TABS : DETENTE_TABS;

  useEffect(() => {
    setLoading(true);
    setError(null);
    setActiveTab('infos');

    const url = `${BACKEND_URL}/api/profiles/player/${encodeURIComponent(pseudo)}?compet=${compet}`;
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) throw new Error(json.error || 'Erreur inconnue');
        setData(json.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [pseudo, compet]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="profile-overlay" onClick={handleOverlayClick}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="profile-header">
          {isCompact && (
            <button className="profile-close" onClick={onClose} title="Retour">
              ←
            </button>
          )}
          <PlayerAvatar pseudo={pseudo} size={48} />
          <div className="profile-header-info">
            <div className="profile-pseudo">{data?.pseudo_display || pseudo}</div>
            {data && (
              <div className="profile-subtitle">
                {data.ville && <span>{data.ville}</span>}
                {data.pays && data.pays !== 'France' && <span>· {data.pays}</span>}
                {!compet && data.teams?.length > 0 && (
                  <>
                    <span>·</span>
                    {data.teams.map((t: { id: number; nom: string }) => (
                      <span
                        key={t.id}
                        className="profile-team-link"
                        onClick={() => onOpenTeam?.(t.id)}
                      >
                        {t.nom}
                      </span>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          {!isCompact && (
            <button className="profile-close" onClick={onClose} title="Fermer">×</button>
          )}
        </div>

        {/* Tabs */}
        {data && (
          <div className="profile-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="profile-body">
          {loading && (
            <div className="profile-loading">
              <div className="profile-spinner" />
              <span>Chargement...</span>
            </div>
          )}

          {error && (
            <div className="profile-error">
              <span>Impossible de charger le profil</span>
              <span style={{ fontSize: 12 }}>{error}</span>
            </div>
          )}

          {data && !loading && !error && (
            <>
              {activeTab === 'infos' && (compet
                ? <CompetInfosTab data={data} />
                : <DetenteInfosTab data={data} onOpenTeam={onOpenTeam} />
              )}
              {activeTab === 'classements' && (compet
                ? <CompetClassementsTab data={data} />
                : <DetenteClassementsTab data={data} />
              )}
              {activeTab === 'stats' && !compet && <DetenteStatsTab data={data} />}
              {activeTab === 'live' && !compet && (
                <DetenteLiveTab data={data} mode={liveMode} onModeChange={setLiveMode} />
              )}
              {activeTab === 'assiduite' && compet && <CompetAssiduiteTab data={data} />}
              {activeTab === 'defis' && compet && <CompetDefisTab data={data} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================
   Détente — Onglet Infos
   ============================================ */

function DetenteInfosTab({ data, onOpenTeam }: { data: ProfileData; onOpenTeam?: (id: string | number) => void }) {
  return (
    <>
      <div className="profile-section">
        <h3 className="profile-section-title">Identité</h3>
        <div className="profile-info-grid">
          {data.prenom && (
            <div className="profile-info-item">
              <span className="profile-info-label">Prénom</span>
              <span className="profile-info-value">{data.prenom}</span>
            </div>
          )}
          {data.nom && (
            <div className="profile-info-item">
              <span className="profile-info-label">Nom</span>
              <span className="profile-info-value">{data.nom}</span>
            </div>
          )}
          {data.sexe && (
            <div className="profile-info-item">
              <span className="profile-info-label">Sexe</span>
              <span className="profile-info-value">{data.sexe === 'M' ? 'Homme' : data.sexe === 'F' ? 'Femme' : data.sexe}</span>
            </div>
          )}
          {data.ville && (
            <div className="profile-info-item">
              <span className="profile-info-label">Ville</span>
              <span className="profile-info-value">{data.ville}</span>
            </div>
          )}
          {data.pays && (
            <div className="profile-info-item">
              <span className="profile-info-label">Pays</span>
              <span className="profile-info-value">{data.pays}</span>
            </div>
          )}
          {data.licence && (
            <div className="profile-info-item">
              <span className="profile-info-label">Licence FFT</span>
              <span className="profile-info-value">{data.licence}</span>
            </div>
          )}
        </div>
      </div>

      <div className="profile-section">
        <h3 className="profile-section-title">Activité</h3>
        <div className="profile-info-grid">
          <div className="profile-info-item">
            <span className="profile-info-label">Inscription</span>
            <span className="profile-info-value">{fmtDate(data.registered_date || data.inscription)}</span>
          </div>
          <div className="profile-info-item">
            <span className="profile-info-label">Dernière connexion</span>
            <span className="profile-info-value">{fmtDate(data.last)}</span>
          </div>
          <div className="profile-info-item">
            <span className="profile-info-label">Statut</span>
            <span className="profile-info-value">
              {data.registered === 'oui' ? 'Enregistré' : data.registered === 'évaluation' ? 'En évaluation' : 'Non enregistré'}
            </span>
          </div>
        </div>
      </div>

      {data.contacts?.email && (
        <div className="profile-section">
          <h3 className="profile-section-title">Contact</h3>
          <div className="profile-contacts">
            <div className="profile-contact-item">
              <span className="profile-contact-type">Email</span>
              <span>{data.contacts.email}</span>
            </div>
          </div>
        </div>
      )}

      {data.teams?.length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section-title">Équipes</h3>
          <div className="profile-member-list">
            {data.teams.map((t: { id: number; nom: string }) => (
              <div
                key={t.id}
                className="profile-member-item"
                onClick={() => onOpenTeam?.(t.id)}
              >
                <div className="profile-member-info">
                  <span className="profile-member-pseudo">{t.nom}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================
   Détente — Onglet Classements
   ============================================ */

function DetenteClassementsTab({ data }: { data: ProfileData }) {
  const c = data.classic;
  if (!c) return <div className="profile-empty">Aucune donnée de classement</div>;

  const rankings = c.rankings;
  const glissant = c.glissant;
  const assiduite = c.assiduite;

  return (
    <>
      {rankings && (
        <div className="profile-section">
          <h3 className="profile-section-title">Points</h3>
          <table className="profile-stats-table">
            <thead>
              <tr>
                <th>Mode</th>
                <th style={{ textAlign: 'right' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {['3', '4', '5'].map((m) => {
                const key = `points${m}`;
                const val = rankings[key];
                return val != null ? (
                  <tr key={m}>
                    <td className="stat-label">{m} joueurs</td>
                    <td className="stat-value">{fmtNum(val)}</td>
                  </tr>
                ) : null;
              })}
            </tbody>
          </table>
        </div>
      )}

      {glissant && (
        <div className="profile-section">
          <h3 className="profile-section-title">Glissant</h3>
          <table className="profile-stats-table">
            <thead>
              <tr>
                <th>Mode</th>
                <th style={{ textAlign: 'right' }}>Moyenne</th>
              </tr>
            </thead>
            <tbody>
              {['3', '4', '5'].map((m) => {
                const val = glissant[m];
                return val != null ? (
                  <tr key={m}>
                    <td className="stat-label">{m} joueurs</td>
                    <td className="stat-value">{fmtNum(val)}</td>
                  </tr>
                ) : null;
              })}
            </tbody>
          </table>
        </div>
      )}

      {assiduite && (
        <div className="profile-section">
          <h3 className="profile-section-title">Assiduité</h3>
          <table className="profile-stats-table">
            <thead>
              <tr>
                <th>Mode</th>
                <th style={{ textAlign: 'right' }}>Parties</th>
                <th style={{ textAlign: 'right' }}>Rang</th>
              </tr>
            </thead>
            <tbody>
              {['3', '4', '5'].map((m) => {
                const a = assiduite[m];
                return a ? (
                  <tr key={m}>
                    <td className="stat-label">{m} joueurs</td>
                    <td className="stat-value">{fmtNum(a.value)}</td>
                    <td className="stat-rank">{a.rank} / {a.effectif}</td>
                  </tr>
                ) : null;
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ============================================
   Détente — Onglet Stats
   ============================================ */

function DetenteStatsTab({ data }: { data: ProfileData }) {
  const c = data.classic;
  if (!c) return <div className="profile-empty">Aucune donnée de stats</div>;

  const jouerie = c.jouerie;
  const pstats = c.pstats;
  const freq = c.frequentation;

  return (
    <>
      {jouerie && (
        <div className="profile-section">
          <h3 className="profile-section-title">Jouerie</h3>
          <table className="profile-stats-table">
            <tbody>
              {jouerie.parties3 != null && (
                <tr>
                  <td className="stat-label">Parties à 3</td>
                  <td className="stat-value">{fmtNum(jouerie.parties3)}</td>
                </tr>
              )}
              {jouerie.parties4 != null && (
                <tr>
                  <td className="stat-label">Parties à 4</td>
                  <td className="stat-value">{fmtNum(jouerie.parties4)}</td>
                </tr>
              )}
              {jouerie.parties5 != null && (
                <tr>
                  <td className="stat-label">Parties à 5</td>
                  <td className="stat-value">{fmtNum(jouerie.parties5)}</td>
                </tr>
              )}
              {jouerie.audace != null && (
                <tr>
                  <td className="stat-label">Audace</td>
                  <td className="stat-value">{fmtPct(jouerie.audace)}</td>
                </tr>
              )}
              {jouerie.reussite_att != null && (
                <tr>
                  <td className="stat-label">Réussite attaque</td>
                  <td className="stat-value">{fmtPct(jouerie.reussite_att)}</td>
                </tr>
              )}
              {jouerie.reussite_def != null && (
                <tr>
                  <td className="stat-label">Réussite défense</td>
                  <td className="stat-value">{fmtPct(jouerie.reussite_def)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pstats && (
        <div className="profile-section">
          <h3 className="profile-section-title">Comportement</h3>
          <table className="profile-stats-table">
            <tbody>
              <tr>
                <td className="stat-label">Parties hébergées</td>
                <td className="stat-value">{fmtNum(pstats.hosted)}</td>
              </tr>
              <tr>
                <td className="stat-label">Kicks</td>
                <td className="stat-value">{fmtNum(pstats.kick)}</td>
              </tr>
              <tr>
                <td className="stat-label">Chelems</td>
                <td className="stat-value">{fmtNum(pstats.chelems)}</td>
              </tr>
              {(pstats.poignee1 > 0 || pstats.poignee2 > 0 || pstats.poignee3 > 0) && (
                <tr>
                  <td className="stat-label">Poignées</td>
                  <td className="stat-value">
                    {fmtNum(pstats.poignee1)} / {fmtNum(pstats.poignee2)} / {fmtNum(pstats.poignee3)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {freq && (
        <div className="profile-section">
          <h3 className="profile-section-title">Fréquentation</h3>
          <div className="profile-info-grid">
            <div className="profile-info-item">
              <span className="profile-info-label">Total</span>
              <span className="profile-info-value">{fmtNum(freq.total)}</span>
            </div>
            {freq.rank && (
              <div className="profile-info-item">
                <span className="profile-info-label">Rang</span>
                <span className="profile-info-value">{freq.rank} / {freq.effectif}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================
   Détente — Onglet Live
   ============================================ */

function DetenteLiveTab({ data, mode, onModeChange }: {
  data: ProfileData;
  mode: '3' | '4' | '5';
  onModeChange: (m: '3' | '4' | '5') => void;
}) {
  const c = data.classic;
  if (!c?.live) return <div className="profile-empty">Aucune donnée Live</div>;

  const live = c.live[mode];

  return (
    <div className="profile-section">
      <h3 className="profile-section-title">Live</h3>
      <div className="profile-mode-selector">
        {(['3', '4', '5'] as const).map((m) => (
          <button
            key={m}
            className={`profile-mode-btn ${mode === m ? 'active' : ''}`}
            onClick={() => onModeChange(m)}
          >
            {m} joueurs
          </button>
        ))}
      </div>

      {live ? (
        <table className="profile-stats-table">
          <tbody>
            {live.pfinal != null && (
              <tr>
                <td className="stat-label">Points final</td>
                <td className="stat-value">{fmtPct(live.pfinal)}</td>
              </tr>
            )}
            {live.patt != null && (
              <tr>
                <td className="stat-label">Attaque</td>
                <td className="stat-value">{fmtPct(live.patt)}</td>
              </tr>
            )}
            {live.pdef != null && (
              <tr>
                <td className="stat-label">Défense</td>
                <td className="stat-value">{fmtPct(live.pdef)}</td>
              </tr>
            )}
            {live.nb != null && (
              <tr>
                <td className="stat-label">Parties jouées</td>
                <td className="stat-value">{fmtNum(live.nb)}</td>
              </tr>
            )}
            {live.victoire != null && (
              <tr>
                <td className="stat-label">Victoires</td>
                <td className="stat-value">{fmtNum(live.victoire)}</td>
              </tr>
            )}
            {live.top5 != null && (
              <tr>
                <td className="stat-label">Top 5</td>
                <td className="stat-value">{fmtNum(live.top5)}</td>
              </tr>
            )}
            {live.presence_total != null && (
              <tr>
                <td className="stat-label">Présence totale</td>
                <td className="stat-value">{fmtNum(live.presence_total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <div className="profile-empty">Aucune donnée Live en mode {mode} joueurs</div>
      )}
    </div>
  );
}

/* ============================================
   Compétition — Onglet Infos
   ============================================ */

function CompetInfosTab({ data }: { data: ProfileData }) {
  const contacts = data.contacts;
  const filteredContacts = contacts?.contacts
    ? Object.entries(contacts.contacts as Record<string, string | null>)
        .filter(([type, val]) => val && !OBSOLETE_CONTACT_TYPES.has(type))
    : [];

  return (
    <>
      <div className="profile-section">
        <h3 className="profile-section-title">Joueur compétition</h3>
        <div className="profile-info-grid">
          <div className="profile-info-item">
            <span className="profile-info-label">Pseudo</span>
            <span className="profile-info-value">{data.pseudo_display || data.pseudo}</span>
          </div>
        </div>
      </div>

      {filteredContacts.length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section-title">Contacts</h3>
          <div className="profile-contacts">
            {filteredContacts.map(([type, val]) => (
              <div key={type} className="profile-contact-item">
                <span className="profile-contact-type">
                  {type === 'wt' ? 'Messagerie WT' : type === 'email' ? 'Email' : type}
                </span>
                <span>{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================
   Compétition — Onglet Classements
   ============================================ */

function CompetClassementsTab({ data }: { data: ProfileData }) {
  const live = data.live;
  const dg = data.differe_general;

  return (
    <>
      {live && (
        <div className="profile-section">
          <h3 className="profile-section-title">Live</h3>
          <table className="profile-stats-table">
            <tbody>
              {live.Patt != null && (
                <tr>
                  <td className="stat-label">Attaque</td>
                  <td className="stat-value">{fmtPct(live.Patt)}</td>
                </tr>
              )}
              {live.Pdef != null && (
                <tr>
                  <td className="stat-label">Défense</td>
                  <td className="stat-value">{fmtPct(live.Pdef)}</td>
                </tr>
              )}
              {live.nb != null && (
                <tr>
                  <td className="stat-label">Parties jouées</td>
                  <td className="stat-value">{fmtNum(live.nb)}</td>
                </tr>
              )}
              {live.victoire != null && (
                <tr>
                  <td className="stat-label">Victoires</td>
                  <td className="stat-value">{fmtNum(live.victoire)}</td>
                </tr>
              )}
              {live.top5 != null && (
                <tr>
                  <td className="stat-label">Top 5</td>
                  <td className="stat-value">{fmtNum(live.top5)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dg && (
        <div className="profile-section">
          <h3 className="profile-section-title">Différé Général</h3>
          {(['normal', 'maxi'] as const).map((type) => {
            const d = dg[type];
            if (!d) return null;
            return (
              <div key={type} style={{ marginBottom: type === 'normal' ? 16 : 0 }}>
                <h4 style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                  {type === 'normal' ? 'Normal' : 'Maxi'}
                </h4>
                <table className="profile-stats-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th style={{ textAlign: 'right' }}>Classement</th>
                      <th style={{ textAlign: 'right' }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="stat-label">Total</td>
                      <td className="stat-value">{fmtNum(d.classement_tot)}</td>
                      <td className="stat-value">{fmtNum(d.score_tot)}</td>
                    </tr>
                    <tr>
                      <td className="stat-label">Attaque</td>
                      <td className="stat-value">{fmtNum(d.classement_att)}</td>
                      <td className="stat-value">{fmtNum(d.score_att)}</td>
                    </tr>
                    <tr>
                      <td className="stat-label">Défense</td>
                      <td className="stat-value">{fmtNum(d.classement_def)}</td>
                      <td className="stat-value">{fmtNum(d.score_def)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {!live && !dg && <div className="profile-empty">Aucune donnée de classement</div>}
    </>
  );
}

/* ============================================
   Compétition — Onglet Assiduité
   ============================================ */

function CompetAssiduiteTab({ data }: { data: ProfileData }) {
  const recent = data.differe_recent;
  const triplNb = data.diff_tripl_nb;

  if (!recent && triplNb == null) {
    return <div className="profile-empty">Aucune donnée d'assiduité</div>;
  }

  return (
    <>
      {recent && Object.keys(recent).length > 0 && (
        <div className="profile-section">
          <h3 className="profile-section-title">Différés récents (90 jours)</h3>
          <table className="profile-stats-table">
            <tbody>
              {Object.entries(recent as Record<string, number>).map(([type, nb]) => (
                <tr key={type}>
                  <td className="stat-label" style={{ textTransform: 'capitalize' }}>{type}</td>
                  <td className="stat-value">{fmtNum(nb)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {triplNb != null && (
        <div className="profile-section">
          <h3 className="profile-section-title">Triplettes différé (90 jours)</h3>
          <div className="profile-info-grid">
            <div className="profile-info-item">
              <span className="profile-info-label">Nombre</span>
              <span className="profile-info-value">{fmtNum(triplNb)}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================
   Compétition — Onglet Défis Libres
   ============================================ */

function CompetDefisTab({ data }: { data: ProfileData }) {
  const defis = data.defis;

  if (!defis) {
    return <div className="profile-empty">Aucun défi récent</div>;
  }

  return (
    <div className="profile-section">
      <h3 className="profile-section-title">Défis Libres (45 jours)</h3>
      <table className="profile-stats-table">
        <tbody>
          <tr>
            <td className="stat-label">Défis joués</td>
            <td className="stat-value">{fmtNum(defis.nb)}</td>
          </tr>
          <tr>
            <td className="stat-label">Victoires</td>
            <td className="stat-value">{fmtNum(defis.victoires)}</td>
          </tr>
          <tr>
            <td className="stat-label">Nuls</td>
            <td className="stat-value">{fmtNum(defis.nuls)}</td>
          </tr>
          <tr>
            <td className="stat-label">Défaites</td>
            <td className="stat-value">{fmtNum(defis.defaites)}</td>
          </tr>
          <tr>
            <td className="stat-label">Surcontrats</td>
            <td className="stat-value">{fmtNum(defis.surcontrats)}</td>
          </tr>
          <tr>
            <td className="stat-label">Attaquant tournant</td>
            <td className="stat-value">{fmtNum(defis.attaquant_tournant)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
